import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Building2, Plus, Trash2 } from 'lucide-react';

const SelectionCopropriete = ({ user, onUpdate }) => {
  const navigate = useNavigate();
  const [coproprietes, setCoproprietes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoproprietes();
  }, []);

  const fetchCoproprietes = async () => {
    try {
      const response = await apiClient.get('/coproprietes');
      setCoproprietes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const selectCopropriete = async (coproId) => {
    try {
      const response = await apiClient.post(`/auth/switch-copropriete/${coproId}`);
      localStorage.setItem('token', response.data.token);
      
      const userResponse = await apiClient.get('/auth/me');
      onUpdate(userResponse.data);
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      toast.success('Copropriété sélectionnée');
      navigate('/');
    } catch (error) {
      toast.error('Erreur lors de la sélection');
    }
  };

  const deleteCopropriete = async (coproId, coproNom, event) => {
    event.stopPropagation(); // Empêche le clic de sélectionner la copro

    const confirmed = window.confirm(
      `⚠️ SUPPRESSION DÉFINITIVE ⚠️\n\nVous êtes sur le point de supprimer la copropriété :\n"${coproNom}"\n\nCeci supprimera DÉFINITIVEMENT :\n- Tous les copropriétaires\n- Toutes les assemblées générales\n- Tous les appels de fonds\n- Tous les documents\n- TOUTES LES DONNÉES\n\nCette action est IRRÉVERSIBLE.\n\nÊtes-vous ABSOLUMENT certain ?`
    );

    if (!confirmed) return;

    const doubleConfirm = window.prompt(
      `Pour confirmer la suppression de "${coproNom}", tapez :\nSUPPRIMER`
    );

    if (!doubleConfirm || doubleConfirm.trim().toLowerCase() !== 'supprimer') {
      toast.error('Suppression annulée - Tapez "SUPPRIMER" pour confirmer');
      return;
    }

    try {
      await apiClient.delete(`/coproprietes/${coproId}`);
      toast.success('Copropriété supprimée');
      
      // Recharger l'utilisateur
      const userResponse = await apiClient.get('/auth/me');
      onUpdate(userResponse.data);
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      fetchCoproprietes();
      
      // Si plus de copropriétés, rediriger vers création
      if (userResponse.data.coproprietes.length === 0) {
        navigate('/creer-copropriete');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Mes copropriétés</h1>
          <p className="text-slate-500">Sélectionnez une copropriété à gérer</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {coproprietes.map((copro) => (
            <div
              key={copro.id}
              className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow relative group"
            >
              <div 
                onClick={() => selectCopropriete(copro.id)}
                className="cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">{copro.nom}</h3>
                    <p className="text-sm text-slate-500">{copro.adresse}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => deleteCopropriete(copro.id, copro.nom, e)}
                className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 p-2"
                title="Supprimer la copropriété"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        <Button
          onClick={() => navigate('/creer-copropriete')}
          className="w-full bg-slate-900 text-white hover:bg-slate-800"
        >
          <Plus className="w-4 h-4 mr-2" />
          Créer une nouvelle copropriété
        </Button>
      </div>
    </div>
  );
};

export default SelectionCopropriete;
